import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { TranscriptVersionSummary } from "../../../shared/interfaces/Transcript";
import { DialogShell } from "@/shared/ui/DialogShell";

interface TranscriptHistoryDialogProps {
  open: boolean;
  versions: TranscriptVersionSummary[];
  isLoading: boolean;
  /** Version currently shown in the editor (null = unknown). */
  currentVersion: number | null;
  latestVersion: number | null;
  /** Whether there are unsaved edits (switching versions will discard them). */
  isDirty: boolean;
  /** Resolves an editor's user id to a display name (falls back to the id). */
  resolveName?: (userId: string) => string | undefined;
  onSelect: (versionNumber: number) => void;
  onClose: () => void;
}

const formatWhen = (createdAt?: number): string => {
  if (!createdAt) return "";
  try {
    return new Date(createdAt).toLocaleString();
  } catch {
    return "";
  }
};

/**
 * Version-history picker: lists every saved revision (newest first) and lets the
 * user open one in the editor. Editing an older version and saving creates a new
 * version at the head, so viewing history is non-destructive.
 */
export const TranscriptHistoryDialog: React.FC<TranscriptHistoryDialogProps> = ({
  open,
  versions,
  isLoading,
  currentVersion,
  latestVersion,
  isDirty,
  resolveName,
  onSelect,
  onClose,
}) => {
  const sorted = [...versions].sort(
    (a, b) => b.versionNumber - a.versionNumber,
  );

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      size="sm"
      titleId="transcript-history-dialog-title"
    >
      <DialogTitle id="transcript-history-dialog-title" sx={{ pr: 6 }}>
        Version history
      </DialogTitle>
      <DialogContent dividers>
        {isDirty && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            You have unsaved changes. Opening another version will discard them.
          </Alert>
        )}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : sorted.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={2}>
            No saved versions yet.
          </Typography>
        ) : (
          <List disablePadding>
            {sorted.map((v) => {
              const isCurrent = v.versionNumber === currentVersion;
              const isLatest = v.versionNumber === latestVersion;
              return (
                <ListItemButton
                  key={v.versionNumber}
                  selected={isCurrent}
                  onClick={() => onSelect(v.versionNumber)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={600}>
                          Version {v.versionNumber}
                        </Typography>
                        {isLatest && (
                          <Chip size="small" color="primary" label="Latest" />
                        )}
                        {isCurrent && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="Viewing"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      [
                        formatWhen(v.createdAt),
                        v.editedBy
                          ? `by ${resolveName?.(v.editedBy) ?? v.editedBy}`
                          : null,
                        typeof v.wordCount === "number"
                          ? `${v.wordCount} words`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || undefined
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
    </DialogShell>
  );
};
